import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, Copy, Loader2, RefreshCw, Check, AlertTriangle, FileText, User, ArrowRight, Hash, Trash2, ChevronDown, ChevronRight, Search, XCircle, Plus, Calendar, Unlink, Link } from 'lucide-react';
import { clientService } from '../../services/clientService';
import { UnrecognisedConfirmation, DuplicateConfirmation, ExtractedTradeFields, ProcessingFailure, EmailSearchResult, EmailSearchResultMatch } from '../../types/confirmation';
import ConfirmDialog from '../common/ConfirmDialog';
import ExceptionCalendarPicker from '../common/ExceptionCalendarPicker';
import MatchScoreIndicator from '../common/MatchScoreIndicator';
import SubmitAsTradeModal from './SubmitAsTradeModal';
import './ExceptionsModal.css';

interface ExceptionsModalProps {
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
  onRefreshNeeded?: () => void;
  defaultDateFrom?: string;   // YYYY-MM-DD — initial date filter (from page's date range)
  defaultDateTo?: string;     // YYYY-MM-DD — initial date filter end
}

interface ExceptionsData {
  unrecognisedConfirmations: UnrecognisedConfirmation[];
  duplicates: DuplicateConfirmation[];
  processingFailures: ProcessingFailure[];
  totalCount: number;
}


/** Extract YYYY-MM-DD from an emailDate string (may be ISO timestamp or YYYY-MM-DD) */
const extractDatePart = (dateStr: string | undefined): string | null => {
  if (!dateStr || dateStr.length < 10) return null;
  return dateStr.substring(0, 10);
};

/** Check if a date string (YYYY-MM-DD) falls within a from/to range (inclusive) */
const isDateInRange = (dateStr: string | null, from: string | null, to: string | null): boolean => {
  if (!dateStr) return false;
  if (from && dateStr < from) return false;
  if (to && dateStr > to) return false;
  return true;
};

const ExceptionsModal: React.FC<ExceptionsModalProps> = ({
  clientId,
  isOpen,
  onClose,
  onRefreshNeeded,
  defaultDateFrom,
  defaultDateTo,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'unrecognised' | 'duplicates' | 'failures'>('unrecognised');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exceptionsData, setExceptionsData] = useState<ExceptionsData | null>(null);
  // Calendar date filter — defaults to the Confirmations page's active date range
  const [filterDateFrom, setFilterDateFrom] = useState<string | null>(defaultDateFrom || null);
  const [filterDateTo, setFilterDateTo] = useState<string | null>(defaultDateTo || null);
  // Entity filter — only shown when exceptions span multiple entities
  const [filterEntityId, setFilterEntityId] = useState<string | null>(null);
  // Find Confirmation panel (KAN-205a)
  const [showFindPanel, setShowFindPanel] = useState(false);
  // Highlight a specific email in the unrecognised list after navigating from Find panel
  const [highlightEmailId, setHighlightEmailId] = useState<string | null>(null);
  // Sync filter to current page date range every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setFilterDateFrom(defaultDateFrom || null);
      setFilterDateTo(defaultDateTo || null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && clientId) {
      loadExceptions();
    }
  }, [isOpen, clientId]);

  const loadExceptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await clientService.getConfirmationExceptions(clientId);
      if (result.success && result.data) {
        setExceptionsData({
          ...result.data,
          processingFailures: result.data.processingFailures || [],
        });
      } else {
        setError(result.message || t('confirmations.exceptions.loadError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('confirmations.exceptions.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // KAN-178: All handlers now accept emailTradeIndex for trade-level operations.
  // Filter uses both emailId + emailTradeIndex to remove the correct item.

  const handleDismissDuplicate = async (emailId: string, emailTradeIndex: number, entityId?: string): Promise<boolean> => {
    const result = await clientService.acknowledgeDuplicate(entityId || clientId, emailId, emailTradeIndex);
    if (result.success) {
      setExceptionsData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          duplicates: prev.duplicates.filter(d => !(d.emailId === emailId && d.emailTradeIndex === emailTradeIndex)),
          totalCount: prev.totalCount - 1
        };
      });
    }
    return result.success;
  };

  const handleReprocess = async (emailId: string, emailTradeIndex: number, entityId?: string): Promise<{ success: boolean; data?: { matched: boolean; matchesFound?: number } }> => {
    const result = await clientService.reprocessEmail(entityId || clientId, emailId, emailTradeIndex);
    if (result.success && result.data?.matched) {
      setTimeout(() => {
        setExceptionsData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            unrecognisedConfirmations: prev.unrecognisedConfirmations.filter(u => !(u.emailId === emailId && u.emailTradeIndex === emailTradeIndex)),
            totalCount: prev.totalCount - 1
          };
        });
        onRefreshNeeded?.();
      }, 2000);
    }
    return result;
  };

  const handleDismiss = async (emailId: string, emailTradeIndex: number, bankTradeNumber?: string, entityId?: string): Promise<boolean> => {
    const result = await clientService.dismissEmail(entityId || clientId, emailId, emailTradeIndex, bankTradeNumber);
    if (result.success) {
      setExceptionsData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          unrecognisedConfirmations: prev.unrecognisedConfirmations.filter(u => !(u.emailId === emailId && u.emailTradeIndex === emailTradeIndex)),
          totalCount: prev.totalCount - 1
        };
      });
    }
    return result.success;
  };

  const handleManualMatch = async (emailId: string, tradeId: string, emailTradeIndex: number, bankTradeNumber?: string, entityId?: string): Promise<boolean> => {
    const result = await clientService.manualMatch(entityId || clientId, emailId, tradeId, emailTradeIndex, bankTradeNumber);
    if (result.success) {
      setExceptionsData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          unrecognisedConfirmations: prev.unrecognisedConfirmations.filter(u => !(u.emailId === emailId && u.emailTradeIndex === emailTradeIndex)),
          totalCount: prev.totalCount - 1
        };
      });
      onRefreshNeeded?.();
    }
    return result.success;
  };

  const handleSubmitAsTrade = (emailId: string, emailTradeIndex: number) => {
    setExceptionsData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        unrecognisedConfirmations: prev.unrecognisedConfirmations.filter(u => !(u.emailId === emailId && u.emailTradeIndex === emailTradeIndex)),
        totalCount: prev.totalCount - 1
      };
    });
    onRefreshNeeded?.();
  };

  const handleDismissFailure = async (emailId: string, entityId?: string): Promise<boolean> => {
    const result = await clientService.dismissEmail(entityId || clientId, emailId);
    if (result.success) {
      setExceptionsData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          processingFailures: prev.processingFailures.filter(f => f.emailId !== emailId),
          totalCount: prev.totalCount - 1
        };
      });
    }
    return result.success;
  };

  const handleRetryProcessing = async (emailId: string, entityId?: string): Promise<boolean> => {
    const result = await clientService.retryProcessing(entityId || clientId, emailId);
    if (result.success) {
      // Remove from failures list — it's now either unmatched or re-failed
      setExceptionsData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          processingFailures: prev.processingFailures.filter(f => f.emailId !== emailId),
          totalCount: prev.totalCount - 1
        };
      });
      // Reload to pick up any state changes
      setTimeout(() => loadExceptions(), 1000);
    }
    return result.success;
  };

  if (!isOpen) return null;

  // Compute all unique exception dates for the calendar (unfiltered)
  const allUnrecognised = exceptionsData?.unrecognisedConfirmations || [];
  const allDuplicates = exceptionsData?.duplicates || [];
  const allFailures = exceptionsData?.processingFailures || [];
  const totalCount = exceptionsData?.totalCount || 0;

  const exceptionDatesSet = new Set<string>();
  for (const item of allUnrecognised) {
    const d = extractDatePart(item.emailDate);
    if (d) exceptionDatesSet.add(d);
  }
  for (const item of allDuplicates) {
    const d = extractDatePart(item.emailDate);
    if (d) exceptionDatesSet.add(d);
  }
  for (const item of allFailures) {
    const d = extractDatePart(item.emailDate);
    if (d) exceptionDatesSet.add(d);
  }
  const exceptionDates = Array.from(exceptionDatesSet);

  // Compute unique entities across all exceptions
  const entityMap = new Map<string, string>(); // entityId → entityName
  for (const item of [...allUnrecognised, ...allDuplicates, ...allFailures]) {
    if (item.entityId && item.entityName) {
      entityMap.set(item.entityId, item.entityName);
    }
  }
  const entityOptions = Array.from(entityMap.entries()); // [[id, name], ...]
  const showEntityFilter = entityOptions.length > 1;

  // Apply date + entity filters
  const hasDateFilter = filterDateFrom !== null || filterDateTo !== null;
  const applyFilters = <T extends { emailDate?: string; entityId?: string }>(items: T[]): T[] => {
    let result = items;
    if (hasDateFilter) {
      result = result.filter(item => isDateInRange(extractDatePart(item.emailDate), filterDateFrom, filterDateTo));
    }
    if (filterEntityId) {
      result = result.filter(item => item.entityId === filterEntityId);
    }
    return result;
  };
  const filteredUnrecognised = applyFilters(allUnrecognised);
  const filteredDuplicates = applyFilters(allDuplicates);
  const filteredFailures = applyFilters(allFailures);

  const unrecognisedCount = filteredUnrecognised.length;
  const duplicatesCount = filteredDuplicates.length;
  const failuresCount = filteredFailures.length;

  const handleCalendarDateSelect = (date: string | null) => {
    if (date) {
      setFilterDateFrom(date);
      setFilterDateTo(date);
    } else {
      setFilterDateFrom(null);
      setFilterDateTo(null);
    }
  };

  return (
    <div className="exceptions-modal-overlay" onClick={onClose}>
      <div className="exceptions-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="exceptions-modal-header">
          <div className="header-title-area">
            <AlertTriangle size={22} className="header-icon" />
            <h2>{t('confirmations.exceptions.title')}</h2>
          </div>
          <div className="header-actions">
            {showEntityFilter && (
              <select
                className="entity-filter-select"
                value={filterEntityId || ''}
                onChange={(e) => setFilterEntityId(e.target.value || null)}
              >
                <option value="">{t('confirmations.exceptions.allEntities', 'All entities')}</option>
                {entityOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            )}
            <button
              className={`exceptions-header-icon-btn${showFindPanel ? ' active' : ''}`}
              onClick={() => setShowFindPanel(prev => !prev)}
              title={t('confirmations.exceptions.findTooltip', 'Find Confirmation')}
            >
              <Search size={18} />
            </button>
            <ExceptionCalendarPicker
              totalCount={totalCount}
              exceptionDates={exceptionDates}
              defaultDateFrom={defaultDateFrom}
              defaultDateTo={defaultDateTo}
              selectedDate={filterDateFrom === filterDateTo ? filterDateFrom : null}
              onDateSelect={handleCalendarDateSelect}
            />
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body: tabs + content, with Find panel overlaid when active */}
        <div className="exceptions-body">
          {showFindPanel && (
            <FindConfirmationPanel
              clientId={clientId}
              onClose={() => setShowFindPanel(false)}
              onGoToException={(emailId) => {
                setShowFindPanel(false);
                setActiveTab('unrecognised');
                setHighlightEmailId(emailId);
                // Clear highlight after animation completes
                setTimeout(() => setHighlightEmailId(null), 3000);
              }}
            />
          )}

          {/* Tabs */}
          <div className="exceptions-tabs">
            <button
              className={`exceptions-tab ${activeTab === 'unrecognised' ? 'active' : ''}`}
              onClick={() => setActiveTab('unrecognised')}
            >
              <Mail size={18} />
              <span>{t('confirmations.exceptions.unrecognisedTab')}</span>
              {unrecognisedCount > 0 && (
                <span className="tab-badge">{unrecognisedCount}</span>
              )}
            </button>
            <button
              className={`exceptions-tab ${activeTab === 'duplicates' ? 'active' : ''}`}
              onClick={() => setActiveTab('duplicates')}
            >
              <Copy size={18} />
              <span>{t('confirmations.exceptions.duplicatesTab')}</span>
              {duplicatesCount > 0 && (
                <span className="tab-badge warning">{duplicatesCount}</span>
              )}
            </button>
            <button
              className={`exceptions-tab ${activeTab === 'failures' ? 'active' : ''}`}
              onClick={() => setActiveTab('failures')}
            >
              <XCircle size={18} />
              <span>{t('confirmations.exceptions.processingFailuresTab')}</span>
              {failuresCount > 0 && (
                <span className="tab-badge error">{failuresCount}</span>
              )}
            </button>
          </div>

          {/* Content */}
          <div className="exceptions-content">
            {loading ? (
              <div className="exceptions-loading">
                <Loader2 size={40} className="spinner-icon" />
                <p>{t('common.loading')}</p>
              </div>
            ) : error ? (
              <div className="exceptions-error">
                <AlertTriangle size={40} />
                <p>{error}</p>
                <button onClick={loadExceptions}>{t('common.retry')}</button>
              </div>
            ) : activeTab === 'unrecognised' ? (
              <UnrecognisedList
                clientId={clientId}
                items={filteredUnrecognised}
                onReprocess={handleReprocess}
                onDismiss={handleDismiss}
                onManualMatch={handleManualMatch}
                onSubmitAsTrade={handleSubmitAsTrade}
                highlightEmailId={highlightEmailId}
              />
            ) : activeTab === 'duplicates' ? (
              <DuplicatesList
                items={filteredDuplicates}
                onDismiss={handleDismissDuplicate}
              />
            ) : (
              <ProcessingFailuresList
                items={filteredFailures}
                onRetry={handleRetryProcessing}
                onDismiss={handleDismissFailure}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== Find Confirmation Panel (KAN-205a) ==========

interface FindConfirmationPanelProps {
  clientId: string;
  onClose: () => void;
  onGoToException: (emailId: string) => void;
}

/** Convert YYYY-MM-DD → DD/MM/YYYY for display. Passes through other formats unchanged. */
const formatChileDate = (dateStr: string | undefined): string => {
  if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr ?? '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const FindConfirmationPanel: React.FC<FindConfirmationPanelProps> = ({ clientId, onClose, onGoToException }) => {
  const { t } = useTranslation();
  const [btn, setBtn] = useState('');
  const [subject, setSubject] = useState('');
  const [sender, setSender] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [results, setResults] = useState<EmailSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  // §14o: per-match state (composite key `${emailId}__${emailTradeIndex ?? 'x'}`)
  const [releasePending, setReleasePending] = useState<{
    emailId: string; entityId: string; emailTradeIndex?: number; workflowState?: string;
  } | null>(null);
  const [releaseLoading, setReleaseLoading] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  // Composite key of the match whose comparison panel is currently expanded
  const [expandedMatchKey, setExpandedMatchKey] = useState<string | null>(null);

  const canSearch = btn.trim() || subject.trim() || sender.trim() || dateFrom || dateTo || timeFrom || timeTo;
  const hasAnything = canSearch || searched;

  const handleClear = () => {
    setBtn('');
    setSubject('');
    setSender('');
    setDateFrom('');
    setDateTo('');
    setTimeFrom('');
    setTimeTo('');
    setResults([]);
    setSearched(false);
    setError('');
  };

  const handleSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    setError('');
    try {
      const result = await clientService.searchConfirmationEmails(clientId, {
        btn: btn.trim() || undefined,
        subject: subject.trim() || undefined,
        sender: sender.trim() || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        timeFrom: timeFrom || undefined,
        timeTo: timeTo || undefined,
      });
      if (result.success) {
        setResults(result.data ?? []);
        setSearched(true);
      } else {
        setError(result.message || t('confirmations.exceptions.loadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseConfirmed = async () => {
    if (!releasePending) return;
    const { emailId, entityId, emailTradeIndex } = releasePending;
    const matchKey = `${emailId}__${emailTradeIndex ?? 'x'}`;
    setReleaseLoading(matchKey);
    try {
      const result = await clientService.releaseEmailToExceptions(entityId, emailId, emailTradeIndex);
      if (result.success) {
        setResults(prev => prev.map(r => {
          if (r.emailId !== emailId) return r;
          const newMatches = (r.matches ?? []).filter(
            (m: EmailSearchResultMatch) => m.emailTradeIndex !== emailTradeIndex
          );
          if (newMatches.length === 0) {
            return { ...r, matchStatus: 'unrecognised' as const, matches: [] };
          }
          return { ...r, matches: newMatches };
        }));
      } else {
        setError(result.message || t('confirmations.exceptions.findReleaseError'));
      }
    } finally {
      setReleaseLoading(null);
    }
  };

  // §14o: matched/superseded chips are now rendered per-match in the card body.
  // getStatusChip only handles the remaining non-matched statuses.
  const getStatusChip = (result: EmailSearchResult) => {
    switch (result.matchStatus) {
      case 'unrecognised':
        // Badge navigates to the specific item in the Unrecognised tab
        return (
          <button
            className="find-status-chip find-status-unrecognised find-status-link"
            onClick={() => onGoToException(result.emailId)}
          >
            {t('confirmations.exceptions.findInExceptions')}
          </button>
        );
      case 'dismissed':
        return <span className="find-status-chip find-status-dismissed">{t('confirmations.exceptions.findDismissed')}</span>;
      case 'extraction_failed':
        return <span className="find-status-chip find-status-failed">{t('confirmations.exceptions.findExtractionFailed')}</span>;
      default:
        return <span className="find-status-chip find-status-unmatched">{t('confirmations.exceptions.findUnmatched')}</span>;
    }
  };

  const onEnter = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && canSearch) handleSearch(); };

  return (
    <div className="find-confirmation-panel">
      {/* Panel header */}
      <div className="find-panel-header">
        <span className="find-panel-title">{t('confirmations.exceptions.findTab', 'Find Confirmation')}</span>
        <button className="find-close-btn" onClick={onClose} title={t('common.close', 'Close')}>
          <X size={18} />
        </button>
      </div>

      {/* Search form */}
      <div className="find-search-form">
        {/* Row 1: three labelled text inputs */}
        <div className="find-search-row">
          <div className="find-input-group">
            <label className="find-input-label">{t('confirmations.exceptions.findBtnLabel', 'Counterparty trade number')}</label>
            <input
              type="text"
              className="find-btn-input"
              value={btn}
              onChange={e => setBtn(e.target.value)}
              onKeyDown={onEnter}
            />
          </div>
          <div className="find-input-group">
            <label className="find-input-label">{t('confirmations.exceptions.findSubjectLabel', 'Email subject')}</label>
            <input
              type="text"
              className="find-btn-input"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              onKeyDown={onEnter}
            />
          </div>
          <div className="find-input-group">
            <label className="find-input-label">{t('confirmations.exceptions.findSenderLabel', 'Email sender')}</label>
            <input
              type="text"
              className="find-btn-input"
              value={sender}
              onChange={e => setSender(e.target.value)}
              onKeyDown={onEnter}
            />
          </div>
        </div>
        {/* Row 2: date/time filters + action buttons */}
        <div className="find-filter-row">
          <div className="find-date-group">
            <label>{t('confirmations.exceptions.findDateFrom', 'From')}</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div className="find-date-group">
            <label>{t('confirmations.exceptions.findDateTo', 'To')}</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <div className="find-date-group">
            <label>{t('confirmations.exceptions.findTimeFrom', 'Time from')}</label>
            <input type="time" value={timeFrom} onChange={e => setTimeFrom(e.target.value)} />
          </div>
          <div className="find-date-group">
            <label>{t('confirmations.exceptions.findTimeTo', 'Time to')}</label>
            <input type="time" value={timeTo} onChange={e => setTimeTo(e.target.value)} />
          </div>
          <div className="find-filter-spacer" />
          <button
            className="find-clear-btn"
            onClick={handleClear}
            disabled={!hasAnything}
          >
            {t('confirmations.exceptions.findClear', 'Clear')}
          </button>
          <button
            className="find-search-btn"
            onClick={handleSearch}
            disabled={!canSearch || loading}
          >
            {loading ? <Loader2 size={16} className="spinner-icon" /> : <Search size={16} />}
            <span>{t('confirmations.exceptions.findSearchButton', 'Search')}</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="find-error">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Results */}
      <div className="find-results">
        {searched && results.length === 0 && !loading && (
          <div className="exceptions-empty">
            <Mail size={56} strokeWidth={1} />
            <p>{t('confirmations.exceptions.findNoResults', 'No confirmation emails found')}</p>
          </div>
        )}

        {/* Main results (non-dismissed) */}
        {results.filter(r => r.matchStatus !== 'dismissed').map(result => (
          <div key={result.emailId} className="find-result-card">
            <div className="find-result-meta">
              <span className="find-result-date">{formatChileDate(result.emailDate)} {result.emailTime}</span>
              <span className="find-result-sender">{result.senderEmail}</span>
              <span className="find-result-subject">{result.subject}</span>
              {result.entityName && (
                <span className="find-result-entity">{result.entityName}</span>
              )}
            </div>
            <div className="find-result-trades">
              {result.extractedTrades.map((trade, i) => (
                <div key={i} className="find-result-trade-row">
                  {trade.BankTradeNumber && <span className="find-trade-btn"><Hash size={12} />{trade.BankTradeNumber}</span>}
                  {trade.Direction && trade.Currency1 && (
                    <span className="find-trade-detail">
                      {trade.Direction} {trade.Currency1} {trade.QuantityCurrency1?.toLocaleString()}
                      {trade.Price ? ` @ ${trade.Price}` : ''}
                    </span>
                  )}
                  {trade.MaturityDate && (
                    <span className="find-trade-detail">
                      {t('confirmations.exceptions.findMaturity', 'Maturity')}: {formatChileDate(trade.MaturityDate)}
                    </span>
                  )}
                  {trade.SettlementType && (
                    <span className="find-trade-detail">{trade.SettlementType}</span>
                  )}
                </div>
              ))}
            </div>
            {/* §14o: matched/superseded — one chip + comparison expand + Desvincular per match */}
            {(result.matchStatus === 'matched' || result.matchStatus === 'superseded')
              ? (result.matches ?? []).map((match: EmailSearchResultMatch) => {
                  const matchKey = `${result.emailId}__${match.emailTradeIndex ?? 'x'}`;
                  const isExpanded = expandedMatchKey === matchKey;
                  const emailTrade = result.extractedTrades[match.emailTradeIndex ?? 0];
                  return (
                    <React.Fragment key={matchKey}>
                      {isExpanded && match.linkedTradeFields && (
                        <div className="find-match-detail">
                          <div className="find-match-col">
                            <div className="find-match-col-title">{t('confirmations.exceptions.findMatchEmailSide', 'Confirmation email says')}</div>
                            {emailTrade && (
                              <table className="find-match-table">
                                <tbody>
                                  {emailTrade.TradeDate    && <tr><td>{t('confirmations.comparisonFields.tradeDate',     'Trade date')}</td><td>{formatChileDate(emailTrade.TradeDate)}</td></tr>}
                                  {emailTrade.CounterpartyName && <tr><td>{t('confirmations.comparisonFields.counterparty','Counterparty')}</td><td>{emailTrade.CounterpartyName}</td></tr>}
                                  {emailTrade.Direction    && <tr><td>{t('confirmations.comparisonFields.direction',     'Direction')}</td><td>{emailTrade.Direction}</td></tr>}
                                  {emailTrade.Currency1    && <tr><td>{t('confirmations.comparisonFields.currency1',     'Currency')}</td><td>{emailTrade.Currency1}{emailTrade.Currency2 ? `/${emailTrade.Currency2}` : ''}</td></tr>}
                                  {emailTrade.QuantityCurrency1 != null && <tr><td>{t('confirmations.comparisonFields.amount', 'Amount')}</td><td>{Number(emailTrade.QuantityCurrency1).toLocaleString()}</td></tr>}
                                  {emailTrade.Price        && <tr><td>{t('confirmations.comparisonFields.price',         'Price')}</td><td>{emailTrade.Price}</td></tr>}
                                  {emailTrade.MaturityDate && <tr><td>{t('confirmations.comparisonFields.maturityDate',  'Maturity')}</td><td>{formatChileDate(emailTrade.MaturityDate)}</td></tr>}
                                  {emailTrade.SettlementType && <tr><td>{t('confirmations.comparisonFields.settlementType','Settlement')}</td><td>{emailTrade.SettlementType}</td></tr>}
                                  {emailTrade.BankTradeNumber && (
                                    <tr className="find-match-cpty-ref-row">
                                      <td>{t('confirmations.exceptions.findMatchCptyRef', 'Cpty ref.')}</td>
                                      <td className="find-match-cpty-ref-val">{emailTrade.BankTradeNumber}</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            )}
                          </div>
                          <div className="find-match-divider" />
                          <div className="find-match-col">
                            <div className="find-match-col-title">{t('confirmations.exceptions.findMatchTradeSide', 'Trade #{{n}} in system', { n: match.linkedTradeNumber })}</div>
                            {(() => {
                              const tf = match.linkedTradeFields!;
                              return (
                                <table className="find-match-table">
                                  <tbody>
                                    {tf.TradeDate    && <tr><td>{t('confirmations.comparisonFields.tradeDate',     'Trade date')}</td><td>{formatChileDate(tf.TradeDate)}</td></tr>}
                                    {tf.CounterpartyName && <tr><td>{t('confirmations.comparisonFields.counterparty','Counterparty')}</td><td>{tf.CounterpartyName}</td></tr>}
                                    {tf.Direction    && <tr><td>{t('confirmations.comparisonFields.direction',     'Direction')}</td><td>{tf.Direction}</td></tr>}
                                    {tf.Currency1    && <tr><td>{t('confirmations.comparisonFields.currency1',     'Currency')}</td><td>{tf.Currency1}{tf.Currency2 ? `/${tf.Currency2}` : ''}</td></tr>}
                                    {tf.QuantityCurrency1 != null && <tr><td>{t('confirmations.comparisonFields.amount', 'Amount')}</td><td>{Number(tf.QuantityCurrency1).toLocaleString()}</td></tr>}
                                    {tf.Price        && <tr><td>{t('confirmations.comparisonFields.price',         'Price')}</td><td>{tf.Price}</td></tr>}
                                    {tf.MaturityDate && <tr><td>{t('confirmations.comparisonFields.maturityDate',  'Maturity')}</td><td>{formatChileDate(tf.MaturityDate)}</td></tr>}
                                    {tf.SettlementType && <tr><td>{t('confirmations.comparisonFields.settlementType','Settlement')}</td><td>{tf.SettlementType}</td></tr>}
                                    {tf.workflowState && <tr><td>{t('confirmations.comparisonFields.workflowState','Status')}</td><td className="find-match-state">{tf.workflowState}</td></tr>}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      <div className="find-result-actions">
                        <button
                          className={`find-status-chip find-status-${result.matchStatus} find-status-link${isExpanded ? ' active' : ''}`}
                          onClick={() => setExpandedMatchKey(isExpanded ? null : matchKey)}
                          title={t('confirmations.exceptions.findMatchClickHint', 'Click to see match details')}
                        >
                          {result.matchStatus === 'superseded'
                            ? t('confirmations.exceptions.findSuperseded', { tradeNumber: match.linkedTradeNumber || '?' })
                            : t('confirmations.exceptions.findMatchedTo', { tradeNumber: match.linkedTradeNumber || '?' })}
                          {match.linkedTradeFields?.workflowState && (
                            <span style={{ opacity: 0.7, marginLeft: 4 }}>· {match.linkedTradeFields.workflowState}</span>
                          )}
                          {isExpanded ? <ChevronDown size={11} style={{ marginLeft: 4 }} /> : <ChevronRight size={11} style={{ marginLeft: 4 }} />}
                        </button>
                        {!['ContractSigned', 'Final'].includes(match.linkedTradeFields?.workflowState ?? '') && (
                          <button
                            className="find-unmatch-btn"
                            onClick={() => setReleasePending({
                              emailId: result.emailId,
                              entityId: result.entityId,
                              emailTradeIndex: match.emailTradeIndex,
                              workflowState: match.linkedTradeFields?.workflowState,
                            })}
                            disabled={releaseLoading === matchKey}
                          >
                            {releaseLoading === matchKey
                              ? <Loader2 size={14} className="spinner-icon" />
                              : <Unlink size={14} />}
                            <span>{t('confirmations.unmatch', 'Unmatch')}</span>
                          </button>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })
              : (
                <div className="find-result-actions">
                  {getStatusChip(result)}
                </div>
              )
            }
          </div>
        ))}

        {/* Dismissed emails — collapsed section at bottom */}
        {results.some(r => r.matchStatus === 'dismissed') && (
          <div className="find-dismissed-section">
            <button
              className="find-dismissed-toggle"
              onClick={() => setShowDismissed(prev => !prev)}
            >
              {showDismissed ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span>
                {t('confirmations.exceptions.findDismissedSection', {
                  count: results.filter(r => r.matchStatus === 'dismissed').length,
                  defaultValue: 'Dismissed ({{count}})',
                })}
              </span>
            </button>
            {showDismissed && results.filter(r => r.matchStatus === 'dismissed').map(result => (
              <div key={result.emailId} className="find-result-card find-result-card-dismissed">
                <div className="find-result-meta">
                  <span className="find-result-date">{formatChileDate(result.emailDate)} {result.emailTime}</span>
                  <span className="find-result-sender">{result.senderEmail}</span>
                  <span className="find-result-subject">{result.subject}</span>
                  {result.entityName && (
                    <span className="find-result-entity">{result.entityName}</span>
                  )}
                </div>
                {result.extractedTrades.length > 0 && (
                  <div className="find-result-trades">
                    {result.extractedTrades.map((trade, i) => (
                      <div key={i} className="find-result-trade-row">
                        {trade.BankTradeNumber && <span className="find-trade-btn"><Hash size={12} />{trade.BankTradeNumber}</span>}
                        {trade.Direction && trade.Currency1 && (
                          <span className="find-trade-detail">
                            {trade.Direction} {trade.Currency1} {trade.QuantityCurrency1?.toLocaleString()}
                            {trade.Price ? ` @ ${trade.Price}` : ''}
                          </span>
                        )}
                        {trade.MaturityDate && (
                          <span className="find-trade-detail">
                            {t('confirmations.exceptions.findMaturity', 'Maturity')}: {formatChileDate(trade.MaturityDate)}
                          </span>
                        )}
                        {trade.SettlementType && (
                          <span className="find-trade-detail">{trade.SettlementType}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="find-result-actions">
                  {getStatusChip(result)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Unmatch confirmation dialog */}
      <ConfirmDialog
        isOpen={releasePending !== null}
        title={t('confirmations.unmatchTitle', 'Unmatch Trade')}
        message={releasePending?.workflowState === 'Confirmed'
          ? t('confirmations.exceptions.findReleaseConfirmedMessage', 'This trade is currently Confirmed. Unlinking it will undo the confirmation and return the trade to \'Booked\' status. The confirmation email will appear in Exceptions. Are you sure?')
          : t('confirmations.exceptions.findReleaseConfirmMessage', 'This will return this confirmation to the exceptions tray, where it can be matched manually. If it is the sole match for a trade, that trade will be reset to unmatched status. Continue?')}
        confirmLabel={t('confirmations.unmatch', 'Unmatch')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          handleReleaseConfirmed();
        }}
        onCancel={() => setReleasePending(null)}
        type="warning"
      />
    </div>
  );
};

// Unrecognised confirmations list component
interface UnrecognisedListProps {
  clientId: string;
  items: UnrecognisedConfirmation[];
  onReprocess: (emailId: string, emailTradeIndex: number, entityId?: string) => Promise<{ success: boolean; data?: { matched: boolean; matchesFound?: number } }>;
  onDismiss: (emailId: string, emailTradeIndex: number, bankTradeNumber?: string, entityId?: string) => Promise<boolean>;
  onManualMatch: (emailId: string, tradeId: string, emailTradeIndex: number, bankTradeNumber?: string, entityId?: string) => Promise<boolean>;
  onSubmitAsTrade: (emailId: string, emailTradeIndex: number) => void;
  highlightEmailId?: string | null;
}

const UnrecognisedList: React.FC<UnrecognisedListProps> = ({ clientId, items, onReprocess, onDismiss, onManualMatch, onSubmitAsTrade, highlightEmailId }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="exceptions-empty">
        <Mail size={56} strokeWidth={1} />
        <p>{t('confirmations.exceptions.noUnrecognised')}</p>
      </div>
    );
  }

  return (
    <div className="exceptions-list">
      {items.map((item) => (
        <UnrecognisedItem
          key={`${item.emailId}-${item.emailTradeIndex}`}
          clientId={clientId}
          item={item}
          onReprocess={onReprocess}
          onDismiss={onDismiss}
          onManualMatch={onManualMatch}
          onSubmitAsTrade={onSubmitAsTrade}
          highlight={highlightEmailId === item.emailId}
        />
      ))}
    </div>
  );
};

// Helper to format amount with currency
const ZERO_DECIMAL_CURRENCIES = ['CLP', 'JPY', 'KRW', 'VND', 'PYG'];

const formatAmount = (amount: string | number | undefined, currency?: string) => {
  if (!amount) return null;
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return String(amount);
  const decimals = currency && ZERO_DECIMAL_CURRENCIES.includes(currency) ? 0 : 2;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(numAmount);
  return currency ? `${currency} ${formatted}` : formatted;
};

// Helper to format exception date/time compactly for tile headers
const formatExceptionDateTime = (emailDate?: string, emailTime?: string): string => {
  if (!emailDate) return '';
  const parts = emailDate.split('-');
  if (parts.length < 3) return emailDate;
  const datePart = `${parts[2]}/${parts[1]}`;
  if (emailTime) {
    const timePart = emailTime.substring(0, 5);
    return `${datePart} ${timePart}`;
  }
  return datePart;
};

// Individual unrecognised item - with expand/collapse functionality
const UnrecognisedItem: React.FC<{
  clientId: string;
  item: UnrecognisedConfirmation;
  onReprocess: (emailId: string, emailTradeIndex: number, entityId?: string) => Promise<{ success: boolean; data?: { matched: boolean; matchesFound?: number } }>;
  onDismiss: (emailId: string, emailTradeIndex: number, bankTradeNumber?: string, entityId?: string) => Promise<boolean>;
  onManualMatch: (emailId: string, tradeId: string, emailTradeIndex: number, bankTradeNumber?: string, entityId?: string) => Promise<boolean>;
  onSubmitAsTrade: (emailId: string, emailTradeIndex: number) => void;
  highlight?: boolean;
}> = ({ clientId, item, onReprocess, onDismiss, onManualMatch, onSubmitAsTrade, highlight }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const itemRef = React.useRef<HTMLDivElement>(null);

  // When highlight becomes true, scroll into view
  React.useEffect(() => {
    if (highlight && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlight]);
  const [reprocessing, setReprocessing] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState(false);
  const [reprocessResult, setReprocessResult] = useState<{ matched: boolean; matchesFound?: number } | null>(null);
  const [showTradePicker, setShowTradePicker] = useState(false);
  const [matching, setMatching] = useState(false);
  const [showSubmitAsTradeModal, setShowSubmitAsTradeModal] = useState(false);

  const handleReprocess = async () => {
    setReprocessing(true);
    setReprocessResult(null);
    const result = await onReprocess(item.emailId, item.emailTradeIndex, item.entityId);
    setReprocessing(false);
    if (result.success && result.data) {
      setReprocessResult(result.data);
    }
  };

  const handleDismiss = async () => {
    setDismissing(true);
    setDismissError(false);
    const success = await onDismiss(item.emailId, item.emailTradeIndex, item.extractedTrade?.BankTradeNumber, item.entityId);
    setDismissing(false);
    if (!success) {
      setDismissError(true);
    }
  };

  const handleConfirmMatch = async (tradeId: string) => {
    setMatching(true);
    const success = await onManualMatch(item.emailId, tradeId, item.emailTradeIndex, trade?.BankTradeNumber, item.entityId);
    setMatching(false);
    if (success) {
      setShowTradePicker(false);
    }
  };

  // KAN-178: Each item is now a single trade, not an email with multiple trades
  const trade = item.extractedTrade;
  const tradeSummary = trade ? (
    `${trade.Direction || ''} ${trade.Currency1 || ''}${trade.Currency2 ? '/' + trade.Currency2 : ''}`
  ).trim() : '';

  const isBusy = reprocessing || dismissing || matching;

  return (
    <div
      ref={itemRef}
      className={`exception-card unrecognised ${isExpanded ? 'expanded' : 'collapsed'}${highlight ? ' exceptions-item-highlighted' : ''}`}
    >
      {/* Clickable Header */}
      <div className="exception-card-header clickable" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="email-info-compact">
            <div className="email-sender">
              <User size={14} />
              <span>{item.senderEmail}</span>
            </div>
            {item.processingSource === 'counterparty_api' && (
              <span className="exception-source-badge api">{t('confirmations.exceptions.sourceApi', 'API')}</span>
            )}
            {!isExpanded && tradeSummary && (
              <span className="trade-summary-badge">{tradeSummary}</span>
            )}
            {!isExpanded && trade?.ProductType && (
              <span className="trades-count-badge">{trade.ProductType}</span>
            )}
            {!isExpanded && item.emailDate && (
              <span className="email-date-badge">
                <Calendar size={12} />
                {formatExceptionDateTime(item.emailDate, item.emailTime)}
              </span>
            )}
            {!isExpanded && item.entityName && (
              <span className="entity-name-badge">{item.entityName}</span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <>
          {/* Email Subject */}
          <div className="email-subject-row">
            <FileText size={14} />
            <span title={item.subject}>{item.subject}</span>
          </div>

          {/* Extracted Trade - Single Card */}
          {trade && (
            <div className="extracted-trades-section">
              <div className="trade-cards-grid">
                <TradeCard trade={trade} index={item.emailTradeIndex} />
              </div>
            </div>
          )}

          {/* Trade Picker (inline, shown when "Match to Trade" is clicked) */}
          {showTradePicker ? (
            <TradePicker
              clientId={clientId}
              extractedTrades={trade ? [trade] : []}
              onConfirm={handleConfirmMatch}
              onCancel={() => setShowTradePicker(false)}
              isMatching={matching}
            />
          ) : (
            /* Action Footer — hidden when trade picker is open */
            <div className="exception-card-footer">
              {reprocessResult ? (
                <div className={`reprocess-result-banner ${reprocessResult.matched ? 'success' : 'no-match'}`}>
                  {reprocessResult.matched ? (
                    <>
                      <Check size={18} />
                      <span>{t('confirmations.exceptions.matchFound', { count: reprocessResult.matchesFound || 1 })}</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={18} />
                      <span>{t('confirmations.exceptions.noMatchFound')}</span>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <button
                    className="dismiss-btn"
                    onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                    disabled={isBusy}
                    title={t('confirmations.exceptions.dismissTooltip')}
                  >
                    {dismissing ? (
                      <Loader2 size={16} className="spinner-icon" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    <span>{t('confirmations.exceptions.dismiss')}</span>
                  </button>
                  <button
                    className="reprocess-btn"
                    onClick={(e) => { e.stopPropagation(); handleReprocess(); }}
                    disabled={isBusy}
                  >
                    {reprocessing ? (
                      <Loader2 size={16} className="spinner-icon" />
                    ) : (
                      <RefreshCw size={16} />
                    )}
                    <span>{t('confirmations.exceptions.reprocess')}</span>
                  </button>
                  <button
                    className="match-to-trade-btn"
                    onClick={(e) => { e.stopPropagation(); setShowTradePicker(true); }}
                    disabled={isBusy}
                  >
                    <Link size={16} />
                    <span>{t('confirmations.exceptions.matchToTrade')}</span>
                  </button>
                  <button
                    className="submit-as-trade-btn"
                    onClick={(e) => { e.stopPropagation(); setShowSubmitAsTradeModal(true); }}
                    disabled={isBusy}
                  >
                    <Plus size={16} />
                    <span>{t('confirmations.exceptions.submitAsTrade')}</span>
                  </button>
                </>
              )}
            </div>
          )}

          {dismissError && (
            <div className="reprocess-result-banner no-match">
              <AlertTriangle size={18} />
              <span>{t('confirmations.exceptions.dismissError')}</span>
            </div>
          )}

          {/* Submit as Trade Modal */}
          {showSubmitAsTradeModal && trade && (
            <SubmitAsTradeModal
              isOpen={showSubmitAsTradeModal}
              onClose={() => setShowSubmitAsTradeModal(false)}
              clientId={clientId}
              emailId={item.emailId}
              extractedTrades={[trade]}
              emailTradeIndex={item.emailTradeIndex}
              onTradeCreated={(tradeNumber, matched) => {
                setShowSubmitAsTradeModal(false);
                onSubmitAsTrade(item.emailId, item.emailTradeIndex);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

// Helper to read a field from a trade object that may use PascalCase or camelCase keys
const tf = (trade: Record<string, unknown>, pascal: string, camel: string): string => {
  const val = trade[pascal] ?? trade[camel];
  return val != null ? String(val) : '';
};

// Compute match score between extracted trade fields and a candidate trade
const computeConfirmationMatchScore = (
  extracted: ExtractedTradeFields | undefined,
  trade: Record<string, unknown>,
): number => {
  if (!extracted) return 0;
  let score = 0;
  const MAX_SCORE = 7;
  // 1. Counterparty
  if (extracted.CounterpartyName && tf(trade, 'CounterpartyName', 'counterpartyName')
      && extracted.CounterpartyName.toLowerCase() === tf(trade, 'CounterpartyName', 'counterpartyName').toLowerCase()) score++;
  // 2. Trade Date
  if (extracted.TradeDate && tf(trade, 'TradeDate', 'tradeDate')
      && extracted.TradeDate === tf(trade, 'TradeDate', 'tradeDate')) score++;
  // 3. Direction
  if (extracted.Direction && tf(trade, 'Direction', 'direction')
      && extracted.Direction.toLowerCase() === tf(trade, 'Direction', 'direction').toLowerCase()) score++;
  // 4. Currency Pair
  const excCcy = `${extracted.Currency1 || ''}/${extracted.Currency2 || ''}`;
  const tradeCcy = `${tf(trade, 'Currency1', 'currency1')}/${tf(trade, 'Currency2', 'currency2')}`;
  if (excCcy.length > 1 && excCcy === tradeCcy) score++;
  // 5. Maturity Date
  if (extracted.MaturityDate && tf(trade, 'MaturityDate', 'maturityDate')
      && extracted.MaturityDate === tf(trade, 'MaturityDate', 'maturityDate')) score++;
  // 6. Product Type
  if (extracted.ProductType && tf(trade, 'ProductType', 'productType')
      && extracted.ProductType.toLowerCase() === tf(trade, 'ProductType', 'productType').toLowerCase()) score++;
  // 7. Settlement Type
  if (extracted.SettlementType && tf(trade, 'SettlementType', 'settlementType')
      && extracted.SettlementType.toLowerCase() === tf(trade, 'SettlementType', 'settlementType').toLowerCase()) score++;
  return score;
};

// Trade Picker Component - Inline panel for selecting an unmatched trade
const TradePicker: React.FC<{
  clientId: string;
  extractedTrades?: ExtractedTradeFields[];
  onConfirm: (tradeId: string) => void;
  onCancel: () => void;
  isMatching: boolean;
}> = ({ clientId, extractedTrades, onConfirm, onCancel, isMatching }) => {
  const { t } = useTranslation();
  const [trades, setTrades] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTradeId, setSelectedTradeId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    loadTrades();
  }, [clientId]);

  const loadTrades = async () => {
    setLoading(true);
    try {
      const result = await clientService.getUnmatchedTrades(clientId);
      // Cast to Record<string, unknown>[] — backend returns raw Firestore docs with PascalCase keys
      const raw = (result || []) as unknown as Record<string, unknown>[];
      // Only show trades that are unmatched AND at 'Booked' workflow state
      const unmatched = raw.filter(t => {
        const status = tf(t, 'Status', 'status');
        const workflowState = tf(t, 'WorkflowState', 'workflowState');
        return status === 'unmatched' && workflowState === 'Booked';
      });
      setTrades(unmatched);
    } catch {
      setTrades([]);
    } finally {
      setLoading(false);
    }
  };

  const firstExtracted = extractedTrades?.[0];
  const extractedCptyLower = (firstExtracted?.CounterpartyName || '').toLowerCase();

  const filteredTrades = trades.filter(trade => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      tf(trade, 'TradeNumber', 'tradeNumber').toLowerCase().includes(q) ||
      tf(trade, 'CounterpartyName', 'counterpartyName').toLowerCase().includes(q) ||
      tf(trade, 'Currency1', 'currency1').toLowerCase().includes(q) ||
      tf(trade, 'Currency2', 'currency2').toLowerCase().includes(q) ||
      tf(trade, 'Direction', 'direction').toLowerCase().includes(q)
    );
  });

  // Compute match scores and sort: counterparty match first, then by score descending
  const scoredTrades = filteredTrades.map(trade => ({
    trade,
    score: computeConfirmationMatchScore(firstExtracted, trade),
    cptyMatch: extractedCptyLower
      ? tf(trade, 'CounterpartyName', 'counterpartyName').toLowerCase() === extractedCptyLower
      : false,
  }));
  scoredTrades.sort((a, b) => {
    if (a.cptyMatch !== b.cptyMatch) return a.cptyMatch ? -1 : 1;
    return b.score - a.score;
  });

  return (
    <div className="trade-picker-panel">
      <div className="trade-picker-header">
        <span className="trade-picker-title">{t('confirmations.exceptions.selectTrade')}</span>
        <div className="trade-picker-search">
          <Search size={14} />
          <input
            type="text"
            placeholder={t('confirmations.exceptions.searchTrades')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>

      <div className="trade-picker-list">
        {loading ? (
          <div className="trade-picker-loading">
            <Loader2 size={24} className="spinner-icon" />
          </div>
        ) : scoredTrades.length === 0 ? (
          <div className="trade-picker-empty">
            <p>{trades.length === 0
              ? t('confirmations.exceptions.noUnmatchedTrades')
              : t('confirmations.exceptions.noTradesMatchSearch')
            }</p>
          </div>
        ) : (
          scoredTrades.map(({ trade, score }) => {
            const tradeId = String(trade.id || '');
            const tradeNumber = tf(trade, 'TradeNumber', 'tradeNumber');
            const direction = tf(trade, 'Direction', 'direction');
            const productType = tf(trade, 'ProductType', 'productType');
            const currency1 = tf(trade, 'Currency1', 'currency1');
            const currency2 = tf(trade, 'Currency2', 'currency2');
            const qty1 = trade.QuantityCurrency1 ?? trade.quantityCurrency1;
            const price = Number(trade.ForwardPrice ?? trade.forwardPrice ?? trade.Price ?? trade.price ?? 0);
            const counterparty = tf(trade, 'CounterpartyName', 'counterpartyName');
            const tradeDate = tf(trade, 'TradeDate', 'tradeDate');
            const maturityDate = tf(trade, 'MaturityDate', 'maturityDate');

            return (
              <div
                key={tradeId}
                className={`trade-picker-item ${selectedTradeId === tradeId ? 'selected' : ''}`}
                onClick={(e) => { e.stopPropagation(); setSelectedTradeId(tradeId); }}
              >
                <div className="picker-trade-main">
                  <span className="picker-trade-number">#{tradeNumber}</span>
                  {productType && <span className="picker-trade-type">{productType}</span>}
                  <span className={`picker-trade-direction ${direction.toLowerCase()}`}>
                    {direction}
                  </span>
                  <span className="picker-trade-amount">
                    {formatAmount(qty1 as string | number | undefined, currency1)}
                  </span>
                  {currency2 && (
                    <>
                      <ArrowRight size={12} className="currency-arrow" />
                      <span className="picker-trade-ccy2">{currency2}</span>
                    </>
                  )}
                  {price > 0 && (
                    <span className="picker-trade-price">@ {price}</span>
                  )}
                  {firstExtracted && <MatchScoreIndicator score={score} maxScore={7} />}
                </div>
                <div className="picker-trade-details">
                  <span>{counterparty}</span>
                  {tradeDate && <span><span className="picker-label">{t('grid.columns.tradeDate')}:</span> {tradeDate}</span>}
                  {maturityDate && <span><span className="picker-label">{t('grid.columns.maturityDate')}:</span> {maturityDate}</span>}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="trade-picker-actions">
        <button
          className="dismiss-btn"
          onClick={(e) => { e.stopPropagation(); onCancel(); }}
          disabled={isMatching}
        >
          <span>{t('common.cancel')}</span>
        </button>
        <button
          className="match-confirm-btn"
          onClick={(e) => { e.stopPropagation(); setShowConfirmDialog(true); }}
          disabled={!selectedTradeId || isMatching}
        >
          {isMatching ? (
            <Loader2 size={16} className="spinner-icon" />
          ) : (
            <Check size={16} />
          )}
          <span>{t('confirmations.exceptions.confirmMatch')}</span>
        </button>
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={t('confirmations.exceptions.confirmMatchTitle')}
        message={t('confirmations.exceptions.confirmMatchMessage')}
        confirmLabel={t('confirmations.exceptions.confirmMatch')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => { setShowConfirmDialog(false); selectedTradeId && onConfirm(selectedTradeId); }}
        onCancel={() => setShowConfirmDialog(false)}
        type="info"
      />
    </div>
  );
};

// KAN-178: Format ISO date (YYYY-MM-DD) to DD/MM/YYYY
const formatDate = (dateStr: string | undefined): string | null => {
  if (!dateStr || dateStr.length < 10) return null;
  const parts = dateStr.substring(0, 10).split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

// Trade Card Component - Visual display of trade data
const TradeCard: React.FC<{ trade: ExtractedTradeFields; index: number }> = ({ trade, index }) => {
  const { t } = useTranslation();

  // Determine trade direction color
  const directionClass = trade.Direction?.toLowerCase() === 'buy' ? 'buy' :
                        trade.Direction?.toLowerCase() === 'sell' ? 'sell' : '';

  const isCompensacion = trade.SettlementType?.toLowerCase().includes('compensaci');
  const isAverageRate = trade.IsAverageRateForward === true;

  return (
    <div className="trade-card">
      <div className="trade-card-top">
        <div className="trade-index">#{index + 1}</div>
        {trade.BankTradeNumber && (
          <div className="trade-ref">
            <Hash size={12} />
            {trade.BankTradeNumber}
          </div>
        )}
        {trade.ProductType && (
          <div className="trade-type">{trade.ProductType}</div>
        )}
      </div>

      <div className="trade-card-body">
        {/* Main trade info row */}
        <div className="trade-main-info">
          {trade.Direction && (
            <span className={`trade-direction ${directionClass}`}>
              {trade.Direction}
            </span>
          )}
          {trade.QuantityCurrency1 && (
            <span className="trade-amount">
              {formatAmount(trade.QuantityCurrency1, trade.Currency1)}
            </span>
          )}
          {trade.Currency2 && (
            <>
              <ArrowRight size={14} className="currency-arrow" />
              <span className="trade-amount">
                {trade.QuantityCurrency1 && trade.Price
                  ? (() => {
                      const raw = Number(trade.QuantityCurrency1) * Number(trade.Price);
                      const zeroDecimal = ['CLP', 'JPY', 'KRW', 'VND', 'PYG'];
                      const rounded = zeroDecimal.includes(trade.Currency2 || '') ? Math.round(raw) : Math.round(raw * 100) / 100;
                      return formatAmount(rounded, trade.Currency2);
                    })()
                  : trade.Currency2}
              </span>
            </>
          )}
        </div>

        {/* Additional details */}
        <div className="trade-details">
          {trade.OurParty && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.ourParty', 'Our Party')}:</span>
              <span className="detail-value">{trade.OurParty}</span>
            </div>
          )}
          {trade.CounterpartyName && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.counterparty')}:</span>
              <span className="detail-value">{trade.CounterpartyName}</span>
            </div>
          )}
          {trade.Price && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.price')}:</span>
              <span className="detail-value">{trade.Price}</span>
            </div>
          )}
          {trade.TradeDate && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.tradeDate')}:</span>
              <span className="detail-value">{formatDate(trade.TradeDate) || trade.TradeDate}</span>
            </div>
          )}
          {trade.MaturityDate && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.maturityDate')}:</span>
              <span className="detail-value">{formatDate(trade.MaturityDate) || trade.MaturityDate}</span>
            </div>
          )}
          {trade.SettlementType && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.settlementType')}:</span>
              <span className="detail-value">{trade.SettlementType}</span>
            </div>
          )}
          {isCompensacion && trade.SettlementCurrency && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.settlementCurrency', 'Settlement Ccy')}:</span>
              <span className="detail-value">{trade.SettlementCurrency}</span>
            </div>
          )}
          {isCompensacion && trade.FixingReference && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.fixingReference', 'Fixing Reference')}:</span>
              <span className="detail-value">{trade.FixingReference}</span>
            </div>
          )}
          {isAverageRate && trade.AverageRateFixingDates && trade.AverageRateFixingDates.length > 0 && (
            <div className="trade-detail">
              <span className="detail-label">{t('grid.columns.fixingDates', 'Fixing Dates')}:</span>
              <span className="detail-value">
                {trade.AverageRateFixingDates.map(d => {
                  const formatted = formatDate(d);
                  // Show DD/MM for brevity in the list
                  return formatted ? formatted.substring(0, 5) : d;
                }).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Duplicates list component
interface DuplicatesListProps {
  items: DuplicateConfirmation[];
  onDismiss: (emailId: string, emailTradeIndex: number, entityId?: string) => Promise<boolean>;
}

const DuplicatesList: React.FC<DuplicatesListProps> = ({ items, onDismiss }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="exceptions-empty">
        <Copy size={56} strokeWidth={1} />
        <p>{t('confirmations.exceptions.noDuplicates')}</p>
      </div>
    );
  }

  return (
    <div className="exceptions-list">
      {items.map((item) => (
        <DuplicateItem key={`${item.emailId}-${item.emailTradeIndex}`} item={item} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

// Individual duplicate item - with expand/collapse functionality
const DuplicateItem: React.FC<{
  item: DuplicateConfirmation;
  onDismiss: (emailId: string, emailTradeIndex: number, entityId?: string) => Promise<boolean>;
}> = ({ item, onDismiss }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState(false);

  const handleDismiss = async () => {
    setDismissing(true);
    setDismissError(false);
    const success = await onDismiss(item.emailId, item.emailTradeIndex, item.entityId);
    setDismissing(false);
    if (!success) {
      setDismissError(true);
    }
  };

  // KAN-178: Each item is now a single trade
  const trade = item.extractedTrade;
  const tradeSummary = trade ? (
    `${trade.Direction || ''} ${trade.Currency1 || ''}${trade.Currency2 ? '/' + trade.Currency2 : ''}`
  ).trim() : '';

  return (
    <div className={`exception-card duplicate ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Clickable Header */}
      <div className="exception-card-header clickable" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="email-info-compact">
            <div className="email-sender">
              <User size={14} />
              <span>{item.senderEmail}</span>
            </div>
            {item.processingSource === 'counterparty_api' && (
              <span className="exception-source-badge api">{t('confirmations.exceptions.sourceApi', 'API')}</span>
            )}
            {!isExpanded && (
              <span className="duplicate-badge-compact">
                <Copy size={12} />
                <span>#{item.duplicateTradeNumber}</span>
              </span>
            )}
            {!isExpanded && tradeSummary && (
              <span className="trade-summary-badge">{tradeSummary}</span>
            )}
            {!isExpanded && trade?.ProductType && (
              <span className="trades-count-badge">{trade.ProductType}</span>
            )}
            {!isExpanded && item.emailDate && (
              <span className="email-date-badge">
                <Calendar size={12} />
                {formatExceptionDateTime(item.emailDate, item.emailTime)}
              </span>
            )}
            {!isExpanded && item.entityName && (
              <span className="entity-name-badge">{item.entityName}</span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <>
          {/* Email Subject */}
          <div className="email-subject-row">
            <FileText size={14} />
            <span title={item.subject}>{item.subject}</span>
          </div>

          {/* Duplicate Info Banner */}
          <div className="duplicate-info-section">
            <div className="duplicate-badge">
              <Copy size={16} />
              <span>{t('confirmations.exceptions.duplicateOf')}</span>
            </div>
            <div className="duplicate-trade-ref">
              <Hash size={16} />
              <span>{item.duplicateTradeNumber}</span>
            </div>
            {(item.originalMatchedAt || item.detectedAt) && (
              <div className="duplicate-detected-at">
                <span className="detail-label">
                  {item.originalMatchedAt
                    ? t('confirmations.exceptions.originalMatchDate', 'Original match date')
                    : t('confirmations.exceptions.duplicateDetectedDate', 'Duplicate detected')}:
                </span>
                <span className="detail-value">
                  {(() => {
                    try {
                      const raw = item.originalMatchedAt || item.detectedAt;
                      // Server timestamps are UTC — ensure correct parsing
                      const d = new Date(raw.endsWith('Z') || raw.includes('+') ? raw : raw + 'Z');
                      const pad = (n: number) => String(n).padStart(2, '0');
                      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                    } catch {
                      return item.originalMatchedAt || item.detectedAt;
                    }
                  })()}
                </span>
              </div>
            )}
          </div>

          {/* Extracted Trade - Single Card */}
          {trade && (
            <div className="extracted-trades-section">
              <div className="trade-cards-grid">
                <TradeCard trade={trade} index={item.emailTradeIndex} />
              </div>
            </div>
          )}

          {/* Action Footer */}
          <div className="exception-card-footer">
            <button
              className="dismiss-btn"
              onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
              disabled={dismissing}
              title={t('confirmations.exceptions.dismissTooltip')}
            >
              {dismissing ? (
                <Loader2 size={16} className="spinner-icon" />
              ) : (
                <Trash2 size={16} />
              )}
              <span>{t('confirmations.exceptions.dismiss')}</span>
            </button>
          </div>

          {dismissError && (
            <div className="reprocess-result-banner no-match">
              <AlertTriangle size={18} />
              <span>{t('confirmations.exceptions.dismissError')}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Processing Failures list component
interface ProcessingFailuresListProps {
  items: ProcessingFailure[];
  onRetry: (emailId: string, entityId?: string) => Promise<boolean>;
  onDismiss: (emailId: string, entityId?: string) => Promise<boolean>;
}

const ProcessingFailuresList: React.FC<ProcessingFailuresListProps> = ({ items, onRetry, onDismiss }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="exceptions-empty">
        <XCircle size={56} strokeWidth={1} />
        <p>{t('confirmations.exceptions.noProcessingFailures')}</p>
      </div>
    );
  }

  return (
    <div className="exceptions-list">
      {items.map((item) => (
        <ProcessingFailedItem key={item.emailId} item={item} onRetry={onRetry} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

// Individual processing failure item
const ProcessingFailedItem: React.FC<{
  item: ProcessingFailure;
  onRetry: (emailId: string, entityId?: string) => Promise<boolean>;
  onDismiss: (emailId: string, entityId?: string) => Promise<boolean>;
}> = ({ item, onRetry, onDismiss }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [retryResult, setRetryResult] = useState<'success' | 'failed' | null>(null);

  const handleRetry = async () => {
    setRetrying(true);
    setRetryResult(null);
    const success = await onRetry(item.emailId, item.entityId);
    setRetrying(false);
    setRetryResult(success ? 'success' : 'failed');
  };

  const handleDismiss = async () => {
    setDismissing(true);
    await onDismiss(item.emailId, item.entityId);
    setDismissing(false);
  };

  const isBusy = retrying || dismissing;

  return (
    <div className={`exception-card processing-failure ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Clickable Header */}
      <div className="exception-card-header clickable" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="email-info-compact">
            <div className="email-sender">
              <User size={14} />
              <span>{item.senderEmail}</span>
            </div>
            {item.processingSource === 'counterparty_api' && (
              <span className="exception-source-badge api">{t('confirmations.exceptions.sourceApi', 'API')}</span>
            )}
            {!isExpanded && item.filename && (
              <span className="trade-summary-badge">{item.filename}</span>
            )}
            {!isExpanded && item.emailDate && (
              <span className="email-date-badge">
                <Calendar size={12} />
                {formatExceptionDateTime(item.emailDate, item.emailTime)}
              </span>
            )}
            {!isExpanded && item.entityName && (
              <span className="entity-name-badge">{item.entityName}</span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <>
          {/* Email Subject */}
          <div className="email-subject-row">
            <FileText size={14} />
            <span title={item.subject}>{item.subject}</span>
          </div>

          {/* Error Details */}
          <div className="processing-error-section">
            {item.filename && (
              <div className="error-detail-row">
                <span className="detail-label">{t('confirmations.exceptions.filename')}:</span>
                <span className="detail-value">{item.filename}</span>
              </div>
            )}
            <div className="error-detail-row">
              <span className="detail-label">{t('confirmations.exceptions.processingError')}:</span>
              <span className="detail-value error-text">{item.processingError}</span>
            </div>
          </div>

          {/* Action Footer */}
          <div className="exception-card-footer">
            {retryResult ? (
              <div className={`reprocess-result-banner ${retryResult === 'success' ? 'success' : 'no-match'}`}>
                {retryResult === 'success' ? (
                  <>
                    <Check size={18} />
                    <span>{t('confirmations.exceptions.retrySuccess')}</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={18} />
                    <span>{t('confirmations.exceptions.retryFailed')}</span>
                  </>
                )}
              </div>
            ) : (
              <>
                <button
                  className="dismiss-btn"
                  onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                  disabled={isBusy}
                  title={t('confirmations.exceptions.dismissTooltip')}
                >
                  {dismissing ? (
                    <Loader2 size={16} className="spinner-icon" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  <span>{t('confirmations.exceptions.dismiss')}</span>
                </button>
                <button
                  className="reprocess-btn"
                  onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                  disabled={isBusy}
                >
                  {retrying ? (
                    <Loader2 size={16} className="spinner-icon" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  <span>{t('confirmations.exceptions.retryProcessing')}</span>
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};


export default ExceptionsModal;
